import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';

interface CurrentUser {
  github_id: string;
  login: string;
  admin: boolean;
  orgs: string[];
}

interface AuthContextType {
  user: CurrentUser | null;
  token: string | null;
  authRequired: boolean;
  githubOAuthConfigured: boolean;
  activeOrg: string | null;
  setActiveOrg: (org: string) => void;
  logout: () => void;
  loading: boolean;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  token: null,
  authRequired: false,
  githubOAuthConfigured: false,
  activeOrg: null,
  setActiveOrg: () => {},
  logout: () => {},
  loading: true,
});

function parseJwtPayload(token: string): CurrentUser | null {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    const payload = JSON.parse(atob(parts[1]));
    return {
      github_id: payload.sub || '',
      login: payload.login || '',
      admin: payload.admin || false,
      orgs: payload.orgs || [],
    };
  } catch {
    return null;
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [token, setToken] = useState<string | null>(null);
  const [user, setUser] = useState<CurrentUser | null>(null);
  const [authRequired, setAuthRequired] = useState(false);
  const [githubOAuthConfigured, setGithubOAuthConfigured] = useState(false);
  const [activeOrg, setActiveOrgState] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  // Check URL for ?token= param (OAuth callback redirect) or localStorage
  useEffect(() => {
    let t: string | null = null;

    // Check hash for token (GitHub OAuth callback redirects to /#?token=...)
    const hash = window.location.hash;
    if (hash.includes('?token=') || hash.includes('&token=')) {
      const hashQuery = hash.includes('?') ? hash.split('?')[1] : '';
      const params = new URLSearchParams(hashQuery);
      const urlToken = params.get('token');
      if (urlToken) {
        t = urlToken;
        localStorage.setItem('opentool_token', t);
        // Set cookie so /internal pages can authenticate via browser navigation
        document.cookie = `ot_session=${t}; path=/; max-age=86400; samesite=lax${window.location.protocol === 'https:' ? '; secure' : ''}`;
        // Clean the token from the URL
        window.location.hash = '';
      }
    }

    if (!t) {
      t = localStorage.getItem('opentool_token');
      // Sync cookie for /internal page auth
      if (t) {
        document.cookie = `ot_session=${t}; path=/; max-age=86400; samesite=lax${window.location.protocol === 'https:' ? '; secure' : ''}`;
      }
    }

    let tokenValidation: Promise<boolean> = Promise.resolve(false);

    if (t) {
      const parsed = parseJwtPayload(t);
      if (parsed) {
        // Check expiry client-side first (fast reject)
        try {
          const payload = JSON.parse(atob(t.split('.')[1]));
          if (payload.exp && payload.exp * 1000 < Date.now()) {
            localStorage.removeItem('opentool_token');
            t = null;
          }
        } catch { /* ignore */ }
      }
      if (t) {
        // Validate token server-side before trusting it.
        // IMPORTANT: this must complete before setLoading(false) so the
        // app never renders with stale/invalid auth state.
        const validateToken = async (token: string): Promise<boolean> => {
          try {
            const resp = await fetch('/api/auth/me', {
              headers: { Authorization: `Bearer ${token}` },
            });
            if (resp.ok) {
              setToken(token);
              setUser(parseJwtPayload(token));
              // Redirect back to pending invite after OAuth callback
              const pendingInvite = localStorage.getItem('opentool_pending_invite');
              if (pendingInvite) {
                localStorage.removeItem('opentool_pending_invite');
                window.location.hash = `invite/${pendingInvite}`;
              }
              return true;
            } else {
              // Server rejected the token — clear it
              localStorage.removeItem('opentool_token');
              document.cookie = 'ot_session=; path=/; max-age=0';
              return false;
            }
          } catch {
            // Network error — trust the token optimistically so offline works
            setToken(token);
            setUser(parseJwtPayload(token));
            return true;
          }
        };
        tokenValidation = validateToken(t);
      }
    }

    // Restore active org
    const savedOrg = localStorage.getItem('opentool_active_org');
    if (savedOrg) {
      setActiveOrgState(savedOrg);
    }

    // Wait for BOTH status check AND token validation before showing UI
    const statusCheck = fetch('/api/status')
      .then(r => r.json())
      .then(data => {
        setAuthRequired(Boolean(data.auth_required));
        setGithubOAuthConfigured(Boolean(data.github_oauth_configured));
      })
      .catch(() => {});

    Promise.all([statusCheck, tokenValidation]).finally(() => setLoading(false));
  }, []);

  const setActiveOrg = useCallback((org: string) => {
    setActiveOrgState(org);
    localStorage.setItem('opentool_active_org', org);
  }, []);

  const logout = useCallback(() => {
    setToken(null);
    setUser(null);
    setActiveOrgState(null);
    localStorage.removeItem('opentool_token');
    localStorage.removeItem('opentool_active_org');
    window.location.hash = '';
  }, []);

  return (
    <AuthContext.Provider value={{ user, token, authRequired, githubOAuthConfigured, activeOrg, setActiveOrg, logout, loading }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
